import { inventoryApi } from "@/features/inventory/api";
import { categoriesKeys } from "@/features/inventory/queryKeys";
import { roomsApi } from "@/features/room-builder/api";
import { stagingsApi } from "@/features/stagings/api";
import { stagingsKeys } from "@/features/stagings/queryKeys";
import { baseUrl } from "@/lib/http";
import { FURNITURE_STATUS } from "@/types/api";
import type {
  Furniture,
  FurnitureCategory,
  FurnitureItem,
  Room,
  RoomGenericItem,
} from "@/types/api";
import GridViewIcon from "@mui/icons-material/GridView";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import PrintIcon from "@mui/icons-material/Print";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { roomKeys, roomsKeys } from "./queryKeys";

// ─── helpers (shared with detail page) ────────────────────────────────────────

type LocationFilter = "locker" | "other" | "atLocation";

function classifyItem(item: FurnitureItem, roomId: number): LocationFilter {
  if (item.room === roomId) return "atLocation";
  if (item.locationInfo) return "locker";
  return "other";
}

function locationLabel(item: FurnitureItem, roomId: number): string {
  if (item.room === roomId) return "At Location";
  if (item.locationInfo) {
    const loc = item.locationInfo;
    return [loc.bin, loc.slot].filter(Boolean).join(" ") || "Locker";
  }
  if (item.roomInfo?.stagingInfo?.address) return item.roomInfo.stagingInfo.address;
  return "Other";
}

function dimLabel(info?: Furniture | null): string {
  if (!info) return "";
  const parts: string[] = [];
  if (info.width != null) parts.push(`${info.width}W`);
  if (info.height != null) parts.push(`${info.height}H`);
  if (info.length != null) parts.push(`${info.length}D`);
  return parts.join(" ");
}

function thumb(path?: string | null): string | undefined {
  return path ? `${baseUrl}${path}` : undefined;
}

interface PrintConfig {
  showImages: boolean;
  excludedCategories: Set<number>;
  showDimensions: boolean;
  showLocation: boolean;
  showStatus: boolean;
  showNotes: boolean;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function StagingPrintPage() {
  const { id } = useParams<{ id: string }>();
  const stagingId = Number(id);
  const nav = useNavigate();

  const [settingsOpen, setSettingsOpen] = useState(true);
  const [activeFilters, setActiveFilters] = useState<Set<LocationFilter>>(
    () => new Set<LocationFilter>(["locker", "other", "atLocation"]),
  );
  const [showPlaceholders, setShowPlaceholders] = useState(true);
  const [config, setConfig] = useState<PrintConfig>({
    showImages: true,
    excludedCategories: new Set(),
    showDimensions: true,
    showLocation: true,
    showStatus: true,
    showNotes: true,
  });

  const stagingQuery = useQuery({
    queryKey: stagingsKeys.detail(stagingId),
    queryFn: () => stagingsApi.get(stagingId),
    enabled: !Number.isNaN(stagingId),
  });

  const roomsQuery = useQuery({
    queryKey: roomsKeys.list(stagingId),
    queryFn: () => roomsApi.list(stagingId),
    enabled: !Number.isNaN(stagingId),
  });

  const roomDetailQueries = useQueries({
    queries: (roomsQuery.data ?? []).map((room) => ({
      queryKey: roomKeys.detail(room.id),
      queryFn: () => roomsApi.get(room.id),
      enabled: !!roomsQuery.data,
    })),
  });

  const categoriesQuery = useQuery({
    queryKey: categoriesKeys.all,
    queryFn: () => inventoryApi.listCategories(),
  });

  const totals = useMemo(() => {
    const acc = { locker: 0, other: 0, atLocation: 0 };
    for (const q of roomDetailQueries) {
      if (!q.data) continue;
      for (const item of q.data.furnitureItemHistory ?? []) {
        acc[classifyItem(item, q.data.id)]++;
      }
    }
    return acc;
  }, [roomDetailQueries]);

  const toggleFilter = (f: LocationFilter) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(f)) next.delete(f);
      else next.add(f);
      return next;
    });
  };

  const toggleCategory = (catId: number) => {
    setConfig((prev) => {
      const next = new Set(prev.excludedCategories);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return { ...prev, excludedCategories: next };
    });
  };

  const isLoading = stagingQuery.isLoading || roomsQuery.isLoading;
  const staging = stagingQuery.data;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!staging) return null;

  return (
    <Box>
      {/* ── Top bar (hidden on print) ── */}
      <Box
        sx={{
          "@media print": { display: "none" },
          p: { xs: 1.5, sm: 2 },
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "flex-start" }}
          spacing={2}
        >
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Typography variant="h6">Print settings</Typography>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => setSettingsOpen((v) => !v)}
              >
                {settingsOpen ? "Hide" : "Show"}
              </Button>
            </Stack>

            {settingsOpen && (
              <Box>
                {/* Location filter */}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    Location filter
                  </Typography>
                  {(
                    [
                      { key: "locker", label: "Locker" },
                      { key: "other", label: "At other houses" },
                      { key: "atLocation", label: "At location" },
                    ] as const
                  ).map(({ key, label }) => (
                    <Button
                      key={key}
                      size="small"
                      variant={activeFilters.has(key) ? "contained" : "outlined"}
                      color="primary"
                      onClick={() => toggleFilter(key)}
                      sx={{ textTransform: "uppercase", fontWeight: 700 }}
                    >
                      {label} ({totals[key]})
                    </Button>
                  ))}
                </Stack>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showPlaceholders}
                      onChange={(e) => setShowPlaceholders(e.target.checked)}
                    />
                  }
                  label="Show placeholders"
                  sx={{ display: "block", mb: 2 }}
                />

                {/* Information to print */}
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    Information to print:
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.showImages}
                          onChange={(e) =>
                            setConfig((c) => ({ ...c, showImages: e.target.checked }))}
                        />
                      }
                      label="Furniture images"
                    />

                    {config.showImages && (categoriesQuery.data ?? []).length > 0 && (
                      <Box sx={{ pl: { xs: 1, sm: 4 }, display: "flex", flexWrap: "wrap", gap: 0 }}>
                        {/* Placeholders toggle */}
                        <FormControlLabel
                          key="placeholders"
                          control={
                            <Checkbox
                              size="small"
                              checked={!config.excludedCategories.has(-1)}
                              onChange={() => toggleCategory(-1)}
                            />
                          }
                          label={<Typography variant="caption">Placeholders</Typography>}
                          sx={{ width: { xs: "50%", sm: "33%", md: "25%" }, minWidth: 140 }}
                        />
                        {(categoriesQuery.data ?? []).map((cat: FurnitureCategory) => (
                          <FormControlLabel
                            key={cat.id}
                            control={
                              <Checkbox
                                size="small"
                                checked={!config.excludedCategories.has(cat.id)}
                                onChange={() => toggleCategory(cat.id)}
                              />
                            }
                            label={<Typography variant="caption">{cat.name}</Typography>}
                            sx={{ width: { xs: "50%", sm: "33%", md: "25%" }, minWidth: 140 }}
                          />
                        ))}
                      </Box>
                    )}

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.showDimensions}
                          onChange={(e) =>
                            setConfig((c) => ({ ...c, showDimensions: e.target.checked }))}
                        />
                      }
                      label="Dimensions"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.showLocation}
                          onChange={(e) =>
                            setConfig((c) => ({ ...c, showLocation: e.target.checked }))}
                        />
                      }
                      label="Location"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.showStatus}
                          onChange={(e) =>
                            setConfig((c) => ({ ...c, showStatus: e.target.checked }))}
                        />
                      }
                      label="Status"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.showNotes}
                          onChange={(e) =>
                            setConfig((c) => ({ ...c, showNotes: e.target.checked }))}
                        />
                      }
                      label="Notes"
                    />
                  </FormGroup>
                </Paper>
              </Box>
            )}
          </Box>

          {/* Action buttons */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexShrink: 0, pt: 0.5, flexWrap: "wrap", rowGap: 1 }}
            useFlexGap
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
            >
              Print
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<GridViewIcon />}
              onClick={() => void nav(`/staging/${stagingId}?view=grid`)}
            >
              Grid
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<MeetingRoomIcon />}
              onClick={() => void nav(`/staging/${stagingId}/room-builder`)}
            >
              Room builder
            </Button>
            <Button variant="outlined" onClick={() => void nav(`/staging/${stagingId}`)}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* ── Printable content ── */}
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Typography
          variant="h4"
          sx={{
            mb: 3,
            fontWeight: 700,
            fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
            // Render once as a compact header at the top of the first printed
            // page (normal flow, so it does not repeat on later pages).
            "@media print": {
              mb: 2,
              py: 1,
              fontSize: "1rem",
              borderBottom: 1,
              borderColor: "divider",
            },
          }}
        >
          {staging.address}
        </Typography>

        {(roomsQuery.data ?? []).map((roomBasic, i) => {
          const detail = roomDetailQueries[i]?.data;
          if (!detail) return null;
          return (
            <PrintRoomSection
              key={roomBasic.id}
              room={detail}
              activeFilters={activeFilters}
              showPlaceholders={showPlaceholders}
              config={config}
            />
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Print room section ────────────────────────────────────────────────────────

interface PrintRoomProps {
  room: Room;
  activeFilters: Set<LocationFilter>;
  showPlaceholders: boolean;
  config: PrintConfig;
}

function PrintRoomSection({ room, activeFilters, showPlaceholders, config }: PrintRoomProps) {
  const visibleItems = (room.furnitureItemHistory ?? []).filter(
    (it) => activeFilters.size === 0 || activeFilters.has(classifyItem(it, room.id)),
  );
  const placeholders = showPlaceholders ? (room.templateAssoc?.items ?? []) : [];

  if (visibleItems.length === 0 && placeholders.length === 0) return null;

  const showImageCol = config.showImages;

  const columns = [
    ...(showImageCol ? [{ key: "image", label: "IMAGE" }] : []),
    { key: "item", label: "ITEM" },
    ...(config.showLocation ? [{ key: "location", label: "LOCATION" }] : []),
    ...(config.showStatus ? [{ key: "status", label: "STATUS" }] : []),
    ...(config.showNotes ? [{ key: "notes", label: "NOTES" }] : []),
  ];

  return (
    <Box sx={{ mb: 4, pageBreakInside: "avoid" }}>
      <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700 }}>
        {room.name}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                sx={{
                  color: "text.secondary",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  width: col.key === "image"
                    ? 56
                    : col.key === "location"
                    ? 160
                    : col.key === "status"
                    ? 200
                    : undefined,
                }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleItems.map((it) => (
            <PrintItemRow
              key={it.id}
              item={it}
              roomId={room.id}
              config={config}
              columns={columns.map((c) => c.key)}
            />
          ))}
          {placeholders.map((ph) => (
            <PrintPlaceholderRow
              key={ph.id}
              item={ph}
              config={config}
              columns={columns.map((c) => c.key)}
            />
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function PrintItemRow({
  item,
  roomId,
  config,
  columns,
}: {
  item: FurnitureItem;
  roomId: number;
  config: PrintConfig;
  columns: string[];
}) {
  const catId = item.furnitureInfo?.category ?? null;
  const showImg = config.showImages && (catId === null || !config.excludedCategories.has(catId));
  const imgSrc = thumb(
    item.furnitureInfo?.imageFile?.thumbnailPath110
      ?? item.furnitureInfo?.imageFile?.thumbnailPath40,
  );

  return (
    <TableRow>
      {columns.includes("image") && (
        <TableCell sx={{ width: 56 }}>
          {showImg && imgSrc
            ? (
              // A real `src`, not a CSS background: browsers drop background
              // images from printed output unless print-color-adjust overrides
              // them, which is why the thumbnails were blank in print preview.
              <Box
                component="img"
                src={imgSrc}
                alt=""
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 0.5,
                  objectFit: "contain",
                }}
              />
            )
            : (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  border: "1px dashed",
                  borderColor: "grey.300",
                  borderRadius: 0.5,
                }}
              />
            )}
        </TableCell>
      )}
      <TableCell>
        <Typography variant="body2">{item.furnitureInfo?.name ?? "—"}</Typography>
        {config.showDimensions && (
          <Typography variant="caption" color="text.secondary">
            {dimLabel(item.furnitureInfo)}
          </Typography>
        )}
      </TableCell>
      {columns.includes("location") && (
        <TableCell>
          <Typography variant="body2">{locationLabel(item, roomId)}</Typography>
        </TableCell>
      )}
      {columns.includes("status") && (
        <TableCell>
          <Typography variant="body2">
            {FURNITURE_STATUS[item.status as keyof typeof FURNITURE_STATUS] ?? "—"}
          </Typography>
        </TableCell>
      )}
      {columns.includes("notes") && (
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {item.roomNotes || item.notes || ""}
          </Typography>
        </TableCell>
      )}
    </TableRow>
  );
}

function PrintPlaceholderRow({
  item,
  columns,
}: {
  item: RoomGenericItem;
  config?: PrintConfig;
  columns: string[];
}) {
  return (
    <TableRow>
      {columns.includes("image") && (
        <TableCell>
          <Box
            sx={{
              width: 40,
              height: 40,
              border: "2px dashed",
              borderColor: "grey.300",
              borderRadius: 0.5,
            }}
          />
        </TableCell>
      )}
      <TableCell>
        <Typography variant="body2" fontStyle="italic" color="text.secondary">
          {item.name}
          {item.quantity > 1 && ` ×${item.quantity}`}
        </Typography>
      </TableCell>
      {columns.includes("location") && <TableCell />}
      {columns.includes("status") && <TableCell />}
      {columns.includes("notes") && <TableCell />}
    </TableRow>
  );
}
